import { useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { base, clinicId } from "../../../../constants";
import { useQuery } from "react-query";
import moduleRoutes from "../../../../routes/moduleRouteList";
import mastersRouteList from "../../../routeList";
import insuranceMasterRouteList from "../../routeList"; // Fixed typo
import {
  Autocomplete,
  Button,
  Divider,
  IconButton,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CustomTableBody from "../../../../utilis/component/CustomTableBody";
import {
  ArrowBack,
  CheckBox,
  CheckBoxOutlineBlank,
  CheckBoxOutlineBlankOutlined,
  Edit,
} from "@mui/icons-material";
import { MasterTableDataType, SearchParamsType } from "./type";
import { getAllCptGrpups, getRateTableService } from "../service";
import AddOrEdit from "./AddOrEdit";
import { FormOnChangeEventType } from "../../../../EMR/types";
import debounce from "../../../../utilis/methods/networkHandling";
import useAuthentication from "@hooks/useAuthentication";

function InsurancePriceCPTList() {
  const pageTitle = "Insurance Price ";
  const navigate = useNavigate();
  const location = useLocation() as { state?: { data?: any } };
  const refdata: MasterTableDataType | null = location.state?.data ?? null;
  const formContainerRef = useRef<HTMLDivElement | null>(null);
  const [searchParams, setSearchParams] = useState<SearchParamsType>({
    pageNumber: 0,
    pageSize: 25,
    sortField: "",
    sortOrder: "",
    clinicId: clinicId,
    groupId: null,
    filter: {
      search: "",
    },
    refid: refdata?.id || null,
  });
  const [data, setData] = useState<MasterTableDataType | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const { user } = useAuthentication();
  const { data: tableData, isLoading: tableDataLoading } = useQuery(
    ["", searchParams, success],
    async () => getRateTableService({...searchParams,codetype:String(user?.codetype)}),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { data: groupList, isLoading: cptLoading } = useQuery(
    ["getallpricelistcptLoading"],
    () => getAllCptGrpups()
  );

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    pageNumber: number
  ) => {
    setSearchParams((prev) => ({
      ...prev,
      pageNumber,
    }));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSearchParams((prev) => ({
      ...prev,
      pageNumber: 0,
      pageSize: parseInt(event.target.value, 10),
    }));
  };

  const debouncedSearch = useCallback(
    debounce((e: FormOnChangeEventType) => {
      setSearchParams((prev: SearchParamsType) => ({
        ...prev,
        filter: {
          search: e.target?.value || "",
        },
      }));
    }, 500),
    []
  );

  return (
    <div className="p-3">
      <Paper>
        <div ref={formContainerRef}></div>
        <div className="flex justify-between p-3">
          <div className="flex gap-2">
            <Typography variant="h6" color={"default"}>
              {pageTitle} Master
            </Typography>
          </div>
          <Button
            color="error"
            startIcon={<ArrowBack />}
            onClick={() => {
              navigate(
                `${base}${moduleRoutes.mainRoute}/${moduleRoutes.masters}/${mastersRouteList.insuranceMaster}/${insuranceMasterRouteList.insurancePriceMaster}`
              );
            }}
          >
            BACK
          </Button>
        </div>
        <Divider />
        <div className="">
          <div >
            <AddOrEdit
              data={data}
              setData={setData}
              setSuccess={setSuccess}
              success={success}
            />
          </div>
          <div className="flex justify-start items-center gap-2 p-3">
            <Autocomplete
              sx={{ width: "300px" }}
              loading={cptLoading}
              options={groupList?.response ?? []}
              getOptionLabel={(option: {
                cptGroupId: number;
                cptGroupName: string;
              }) => option?.cptGroupName || ""}
              isOptionEqualToValue={(
                option: { cptGroupId: number; cptGroupName: string },
                value: { cptGroupId: number; cptGroupName: string }
              ) => option?.cptGroupId === value?.cptGroupId}
              renderInput={(params) => (
                <TextField {...params} label="cpt group" />
              )}
              onChange={(_, value: { cptGroupId: number } | null) => {
                setSearchParams((prev: SearchParamsType) => ({
                  ...prev,
                  groupId: value?.cptGroupId || null,
                }));
              }}
            />
            <TextField
              sx={{ width: "300px" }}
              onChange={debouncedSearch}
              size="small"
              variant="outlined"
              placeholder="Search"
              label="search"
            />
          </div>
          <TableContainer className="border">
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {headings.map((heading) => (
                    <TableCell key={heading} className="font-bold">
                      {heading}
                    </TableCell>
                  ))}
                  <TableCell className="font-bold">Actions</TableCell>
                </TableRow>
              </TableHead>
              <CustomTableBody
                alert={"No data"}
                dataLoading={tableDataLoading}
                isData={Boolean(tableData?.response?.items?.length)}
                totalSpan={{
                  row: searchParams.pageSize,
                  col: headings.length + 1,
                }}
              >
                {tableData?.response?.items?.map((tableItem, index) => (
                  <TableRow key={`${pageTitle}-${index}`}>
                    <TableCell>{tableItem?.cptGroupName}</TableCell>
                    <TableCell>{tableItem?.cptName}</TableCell>
                    <TableCell>{tableItem?.cptCode}</TableCell>
                    <TableCell>{tableItem?.insRate}</TableCell>
                    <TableCell>{tableItem?.factor}</TableCell>
                    <TableCell>
                      {tableItem?.isExcluded ? (
                        <CheckBox color="info" />
                      ) : (
                        <CheckBoxOutlineBlankOutlined />
                      )}
                    </TableCell>
                    <TableCell>
                      {tableItem?.approvalRequired ? (
                        <CheckBox color="info" />
                      ) : (
                        <CheckBoxOutlineBlank />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <IconButton
                          color="primary"
                          onClick={() => {
                            if (formContainerRef.current) {
                              formContainerRef.current.scrollIntoView({
                                behavior: "smooth",
                              });
                            }
                            setData(tableItem);
                          }}
                        >
                          <Edit />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </CustomTableBody>
            </Table>
            <div className="flex justify-end">
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={tableData?.response?.totalCount || 0}
                rowsPerPage={searchParams.pageSize}
                page={searchParams.pageNumber}
                sx={{ border: 0 }}
                slotProps={{
                  select: {
                    inputProps: {
                      "aria-label": "rows per page",
                    },
                    native: true,
                  },
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>
          </TableContainer>
        </div>
      </Paper>
    </div>
  );
}

export default InsurancePriceCPTList;

const headings = [
  "CPT Group",
  "CPT Name",
  "CPT Code",
  "Rate",
  "Factor",
  "Is Excluded",
  "Approval",
];
